// The words the card and the callouts use for one vehicle, and the one clock
// the whole front end reads an instant by. Pure and DOM-free, so the Polish
// copy can be checked without a browser.

/** Whole minutes off schedule; below two minutes a vehicle counts as on time. */
const offSchedule = (delay: number) => {
  const min = Math.round(delay / 60);
  return Math.abs(min) >= 2 ? min : 0;
};

/**
 * Signed whole minutes off schedule, for the code that needs the number rather
 * than the words; `null` is "the feed said nothing". Deliberately *not* put
 * through `offSchedule`: the threshold is a rule about what counts as late, and
 * the caller applies it, so the fact itself survives (§4.4 `Item.delayMin`).
 */
export const delayMinutes = (delay: number | null): number | null =>
  delay == null ? null : Math.round(delay / 60);

/** The short form riding on the map callout next to the line number. */
export function delayLabel(delay: number | null): string {
  if (delay == null) return '';
  const min = offSchedule(delay);
  return min > 0 ? `+${min} min` : min < 0 ? `${min} min` : '';
}

/**
 * Under this many seconds a reading is live; over it, it is old enough to be
 * dated. The one line for the whole app (#218): a position fix, the vehicle
 * snapshot and every source's band are measured against it, and one app may
 * not hold two ideas of how old "now" is.
 */
export const LIVE_S = 90;

/** Age of the position fix, in the units the resident thinks in. */
function fixAge(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return s < LIVE_S ? `pozycja sprzed ${s} s` : `pozycja sprzed ${Math.round(s / 60)} min`;
}

/** `unknown` is not `onTime`: the feed simply said nothing about this trip's schedule. */
type DelayState = 'onTime' | 'off' | 'unknown';
export type Details = {
  line: string;
  headsign: string;
  kind: string;
  delay: string;
  delayState: DelayState;
  nextStop: string;
};

/**
 * What the detail card is told about one vehicle. Not a `Vehicle`: the card is
 * built from an `Item` (§4.3 — `src/data/data.ts` is the only reader of a wire
 * shape), and these are the fields an `Item` carries or derives.
 */
export type VehicleFacts = {
  line: string;
  /**
   * Absent when the seam gave no mode. The card then says „Linia 16" rather
   * than picking one of the two nouns and being wrong half the time.
   */
  type?: 'tram' | 'bus';
  headsign: string;
  /** Signed whole minutes, `null` when the feed said nothing (§4.4). */
  delayMin: number | null;
  /** Age of the position fix in seconds. */
  fixAgeS: number;
  nextStop?: string;
};

/**
 * Everything the details card says about one vehicle. Pure, so the copy is
 * testable without a DOM.
 */
export function vehicleDetails(v: VehicleFacts): Details {
  const noun = v.type === 'tram' ? 'Tramwaj' : v.type === 'bus' ? 'Autobus' : 'Linia';
  // The threshold is a rule about what counts as late, and it is applied here
  // rather than carried in the number (§4.4).
  const min = v.delayMin == null ? null : offSchedule(v.delayMin * 60);
  const late =
    min == null
      ? ''
      : min > 0
        ? `Opóźnienie ${min} min`
        : min < 0
          ? `${-min} min przed czasem`
          : 'Jedzie o czasie';
  return {
    line: v.line,
    headsign: v.headsign,
    kind: `${noun} ${v.line}, kierunek jazdy`,
    delay: [late, fixAge(v.fixAgeS)].filter(Boolean).join(', '),
    delayState: min == null ? 'unknown' : min === 0 ? 'onTime' : 'off',
    nextStop: v.nextStop ? `Następny przystanek: ${v.nextStop}` : '',
  };
}

// --- Poznań's clock ---------------------------------------------------------

/**
 * The one zone this app writes an absolute time in, named and never inherited.
 *
 * This is an app about one city, and the machine it runs on is not always in
 * it: CI runs in UTC and a resident may read the page from anywhere. „ZTM nie
 * odpowiada od 13:40" has to say 13:40 in Poznań or it is not about the tram
 * they are waiting for.
 *
 * Every formatter in the front end lives in this module and a fitness function
 * in `src/shared/format.test.ts` keeps it that way: five copies of this had grown
 * across the parallel sessions and two of them had lost the zone (#170).
 */
const ZONE = 'Europe/Warsaw';

/**
 * The formatters are built once. `Intl.DateTimeFormat` is expensive enough that
 * constructing one per row per second is a real cost on a rail of seventy.
 */
const HHMM = new Intl.DateTimeFormat('pl-PL', { hour: '2-digit', minute: '2-digit', timeZone: ZONE });

const DDMM = new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: '2-digit', timeZone: ZONE });

/** „13:40" — the hour in Poznań, whoever is reading and wherever they are. */
export const clockLabel = (at: number): string => HHMM.format(at);

/**
 * „15.09" — the day in Poznań. Its one job is deciding whether two instants
 * fall on the same Poznań day: at 23:30 UTC a report from this evening is
 * already „tomorrow" in a zone we are not in.
 */
export const dayLabel = (at: number): string => DDMM.format(at);

/** „15.09, 09:15" — an entry from another day says which day (§7.2). */
export const dateTimeLabel = (at: number): string => `${DDMM.format(at)}, ${HHMM.format(at)}`;

/** The calendar and clock Poznań is reading at this instant. */
const POZNAN = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export type WallClock = { year: number; month: number; day: number; hour: number; minute: number };

/** The wall clock Poznań is showing at `at`, for the code that does arithmetic on it. */
export function poznanWall(at: number): WallClock {
  const parts = new Map<Intl.DateTimeFormatPartTypes, string>(
    POZNAN.formatToParts(at).map((p) => [p.type, p.value]),
  );
  const n = (k: Intl.DateTimeFormatPartTypes) => Number(parts.get(k) ?? 0);
  // `hour12: false` still renders midnight as 24 in some engines.
  return { year: n('year'), month: n('month'), day: n('day'), hour: n('hour') % 24, minute: n('minute') };
}

/** How far ahead of UTC Poznań is at this instant, in milliseconds. */
export function poznanOffset(at: number): number {
  const p = poznanWall(at);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(at / 60_000) * 60_000;
}
